import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { getAuthConfig, getAuthUserId } from '../../../../data/auth/authSelectors'
import { ExtractRTKQueryArgs, ExtractRTKQueryOptions, RTKQueryHook, RTKQueryResultType } from '../../../../data/baseApi'
import {
  useGetConversationsQuery,
  useGetConversationsV2Query,
  useGetPatientConversationsQuery,
  useGetPatientConversationsV2Query,
} from '../messagesApi'
import { getDerivedProperties } from '../utils'

export const useConversationsData: RTKQueryHook<typeof useGetConversationsQuery> = (args, opts) => {
  const userId = useSelector(getAuthUserId)
  const showOverdueMessageFlag = useSelector(getAuthConfig)?.showOverdueMessageFlag
  const { isTwilioDeprecatedForLyraAI } = useFlags()
  const queryArgs = {
    providerId: userId,
    derivedProperties: getDerivedProperties(showOverdueMessageFlag),
    types: CONVERSATION_TYPE.PROVIDER,
    ...args,
  }
  const useV2AiConversations = isTwilioDeprecatedForLyraAI && queryArgs.types === CONVERSATION_TYPE.AI_COACH
  const v2QueryArgs = {
    providerId: queryArgs.providerId,
    type: queryArgs.types,
  }
  const v1Result = useGetConversationsQuery(useV2AiConversations ? skipToken : queryArgs, opts)
  const v2Result = useGetConversationsV2Query(useV2AiConversations ? v2QueryArgs : skipToken, opts)

  return (useV2AiConversations ? v2Result : v1Result) as RTKQueryResultType<typeof useGetConversationsQuery>
}

type PatientConversationsArgs = ExtractRTKQueryArgs<typeof useGetPatientConversationsQuery>
type PatientConversationsOptions = ExtractRTKQueryOptions<typeof useGetPatientConversationsQuery>

/**
 * Routes standard Lyra AI conversation lists to V2 while keeping provider/patient
 * and flag-off traffic on V1. Both hooks are declared to follow the Rules of Hooks;
 * skipToken ensures only the selected transport issues a request.
 */
export const usePatientConversationsData = (
  args: PatientConversationsArgs | typeof skipToken,
  opts?: PatientConversationsOptions,
): RTKQueryResultType<typeof useGetPatientConversationsQuery> => {
  const { isTwilioDeprecatedForLyraAI } = useFlags()
  const useV2AiConversations =
    args !== skipToken && isTwilioDeprecatedForLyraAI && args.types === CONVERSATION_TYPE.AI_COACH
  const v2QueryArgs =
    args === skipToken
      ? skipToken
      : {
          providerId: args.providerId,
          patientId: args.patientId,
          type: args.types,
        }
  const v1Result = useGetPatientConversationsQuery(useV2AiConversations ? skipToken : args, opts)
  const v2Result = useGetPatientConversationsV2Query(useV2AiConversations ? v2QueryArgs : skipToken, opts)

  return (useV2AiConversations ? v2Result : v1Result) as RTKQueryResultType<typeof useGetPatientConversationsQuery>
}
